import { useRef, useEffect, useState } from 'react';

const companyInfo = [
  { label: '会社名', value: '株式会社BotaN' },
  { label: '郵便番号', value: '〒810-0075' },
  { label: '所在地', value: '福岡県福岡市中央区港1-2-18-1712' },
  { label: '電話番号', value: '0120-676-337', href: 'tel:0120-676-337' },
  { label: 'メール', value: 'info@botan-buy.com', href: 'mailto:info@botan-buy.com' },
];

const licenseInfo = [
  { label: '許可区分', value: '古物商許可' },
  { label: '許可機関', value: '福岡県公安委員会' },
  { label: '許可番号', value: '901012610013' },
  { label: '取引銀行', value: '三井住友銀行' },
  { label: '資本金', value: '200万円' },
];

export default function Company() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setTitleVisible(true); },
      { threshold: 0.2 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="company" className="py-24 bg-stone-900">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div ref={titleRef} className="text-center mb-16">
          <span className={`text-amber-400 text-sm font-semibold tracking-widest uppercase mb-3 block transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            COMPANY
          </span>
          <h2
            className={`text-4xl md:text-5xl font-black text-white mb-4 transition-all duration-700 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ fontFamily: '\'Noto Serif JP\', serif' }}
          >
            福岡の買取専門店・会社情報
          </h2>
          <p className={`text-stone-400 text-base max-w-md mx-auto transition-all duration-700 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            福岡県公安委員会の古物商許可を取得した、九州で信頼の出張買取専門店です
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Company Info Card */}
          <CompanyCard title="会社概要" icon="ri-building-line" delay={0} titleVisible={titleVisible}>
            <div className="space-y-4">
              {companyInfo.map((item) => (
                <div key={item.label} className="flex flex-col gap-0.5">
                  <span className="text-stone-500 text-xs font-medium uppercase tracking-wide">{item.label}</span>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-white text-sm font-medium hover:text-amber-400 transition-colors cursor-pointer"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <span className="text-white text-sm font-medium">{item.value}</span>
                  )}
                </div>
              ))}
            </div>
          </CompanyCard>

          {/* License Card */}
          <CompanyCard title="許可・資本金" icon="ri-shield-check-line" delay={100} titleVisible={titleVisible}>
            <div className="space-y-4">
              {licenseInfo.map((item) => (
                <div key={item.label} className="flex flex-col gap-0.5">
                  <span className="text-stone-500 text-xs font-medium uppercase tracking-wide">{item.label}</span>
                  <span className="text-white text-sm font-medium">{item.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-stone-700">
              <div className="flex items-center gap-2 text-amber-400">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-verified-badge-line text-lg" />
                </div>
                <span className="text-sm font-semibold">正規許可取得済み</span>
              </div>
              <p className="text-stone-500 text-xs mt-2 leading-relaxed">
                行政機関の許可を受けた安心・安全な買取業者です。
              </p>
            </div>
          </CompanyCard>

          {/* Trust Card */}
          <CompanyCard title="安心の買取" icon="ri-hand-heart-line" delay={200} titleVisible={titleVisible}>
            <div className="space-y-5">
              {[
                { icon: 'ri-price-tag-3-line', title: '適正価格での査定', desc: '市場価値に基づいた公正な査定価格を提示します' },
                { icon: 'ri-file-text-line', title: '明確な査定説明', desc: '査定根拠を丁寧にご説明いたします' },
                { icon: 'ri-lock-line', title: '個人情報の厳重管理', desc: 'お客様の個人情報は厳重に管理・保護いたします' },
                { icon: 'ri-refresh-line', title: 'キャンセル自由', desc: '査定後のキャンセルも承ります。費用は一切かかりません' },
              ].map((item) => (
                <div key={item.title} className="flex gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 text-base shrink-0 mt-0.5">
                    <i className={item.icon} />
                  </div>
                  <div>
                    <div className="text-white text-sm font-semibold">{item.title}</div>
                    <div className="text-stone-400 text-xs mt-0.5 leading-relaxed">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </CompanyCard>
        </div>
      </div>
    </section>
  );
}

function CompanyCard({
  title,
  icon,
  delay,
  titleVisible,
  children,
}: {
  title: string;
  icon: string;
  delay: number;
  titleVisible: boolean;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`bg-stone-800/60 border border-stone-700/50 rounded-2xl p-7 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-500/20 text-amber-400 text-xl">
          <i className={icon} />
        </div>
        <h3 className="text-white font-bold text-lg">{title}</h3>
      </div>
      {children}
    </div>
  );
}
